/* @jsx h */
import { computed, defineComponent, h, ref, type PropType } from "vue";
import type { GridColumnDef } from "./types";

export const BulkEditBar = defineComponent({
  name: "BulkEditBar",
  props: {
    selectedCount: { type: Number, required: true },
    columns: { type: Array as PropType<GridColumnDef[]>, required: true },
    onApply: {
      type: Function as PropType<(field: string, value: any) => void>,
      required: true,
    },
    onDeleteSelected: { type: Function as PropType<() => void>, required: true },
    onClose: { type: Function as PropType<() => void>, required: true },
  },
  setup(props) {
    const drafts = ref<Record<string, any>>({});

    const bulkCols = computed(() =>
      props.columns.filter((c) => c.bulkEditable && c.type !== "readonly"),
    );

    const apply = (col: GridColumnDef) => {
      if (!(col.key in drafts.value)) return;
      const raw = drafts.value[col.key];
      const value = col.parser ? col.parser(String(raw ?? "")) : raw;
      props.onApply(col.key, value);
      delete drafts.value[col.key];
    };

    const renderField = (col: GridColumnDef) => {
      const hasDraft = col.key in drafts.value;

      const editor = () => {
        switch (col.type) {
          case "select": {
            const options = col.options ?? [];
            return (
              <select
                class="select select-bordered select-sm w-full"
                value={drafts.value[col.key] ?? ""}
                onChange={(e: any) => (drafts.value[col.key] = e.target.value)}
              >
                <option value="">{col.required ? "Select…" : "No change"}</option>
                {options.map((o) => (
                  <option key={String(o.value)} value={String(o.value)}>
                    {o.label}
                  </option>
                ))}
              </select>
            );
          }
          case "boolean":
            return (
              <div class="flex items-center gap-3">
                <input
                  type="checkbox"
                  class="toggle toggle-primary toggle-sm"
                  checked={Boolean(drafts.value[col.key])}
                  onChange={(e: any) => (drafts.value[col.key] = e.target.checked ? 1 : 0)}
                />
                <span class="text-xs text-base-content/50">
                  {drafts.value[col.key] ? "On" : "Off"}
                </span>
              </div>
            );
          case "number":
          case "price":
            return (
              <input
                type="number"
                class="input input-bordered input-sm w-full"
                value={drafts.value[col.key] ?? ""}
                step={col.step ?? "any"}
                min={col.min}
                placeholder={col.placeholder ?? ""}
                onInput={(e: any) => (drafts.value[col.key] = e.target.value)}
              />
            );
          default:
            return (
              <input
                type="text"
                class="input input-bordered input-sm w-full"
                value={drafts.value[col.key] ?? ""}
                placeholder={col.placeholder ?? "Enter value…"}
                onInput={(e: any) => (drafts.value[col.key] = e.target.value)}
              />
            );
        }
      };

      return (
        <div key={col.key} class="space-y-1.5">
          <div class="text-xs font-semibold uppercase tracking-wide text-base-content/45">
            {col.header}
          </div>
          <div class="flex items-center gap-1.5">
            <div class="flex-1">{editor()}</div>
            <button
              class={[
                "btn btn-square btn-sm flex-shrink-0",
                hasDraft ? "btn-primary" : "btn-ghost opacity-40",
              ].join(" ")}
              title={`Apply to ${props.selectedCount} rows`}
              disabled={!hasDraft}
              onClick={() => apply(col)}
            >
              <span class="material-symbols-outlined text-sm">check</span>
            </button>
          </div>
        </div>
      );
    };

    return () => (
      <div class="lg-bulk-bar">
        {/* Header */}
        <div class="flex items-center justify-between px-4 py-3 border-b border-base-300">
          <div>
            <div class="text-sm font-semibold text-base-content">Bulk Edit</div>
            <div class="text-xs text-base-content/45 mt-0.5">
              {props.selectedCount} row{props.selectedCount !== 1 ? "s" : ""} selected
            </div>
          </div>
          <button class="btn btn-ghost btn-xs btn-square" onClick={props.onClose}>
            <span class="material-symbols-outlined text-sm">close</span>
          </button>
        </div>

        {/* Fields */}
        <div class="flex-1 overflow-y-auto p-3 space-y-4">
          {bulkCols.value.length === 0 ? (
            <p class="text-xs text-base-content/40 text-center py-4">
              No bulk-editable fields
            </p>
          ) : (
            bulkCols.value.map(renderField)
          )}
        </div>

        {/* Delete action */}
        <div class="p-3 border-t border-base-300">
          <button
            class="btn btn-sm w-full border-error/25 bg-error/8 text-error hover:bg-error hover:text-error-content shadow-none"
            onClick={props.onDeleteSelected}
          >
            <span class="material-symbols-outlined text-sm">delete</span>
            Delete {props.selectedCount} row{props.selectedCount !== 1 ? "s" : ""}
          </button>
        </div>
      </div>
    );
  },
});
